import classNames from 'classnames';
import { motion } from 'framer-motion';
import React from 'react';
import { forwardRef, useImperativeHandle } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import SearchBarSectionMobileWrapper from '@/components/organisms/search-bar/shared/search-bar-section-mobile-wrapper/search-bar-section-mobile-wrapper';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './search-bar-brand-dropdown.module.scss';

export interface SearchBarBrandDropdownProps {
  /** The class name to apply */
  className?: string;
  /** Whether to remove range styles */
  customRangeStyle?: boolean;
  /** Default number of adults per room */
  description?: string | string[];
  /** Indicates whether the dropdown is visible */
  isVisible?: boolean;
  /** The label to show in the mobile wrapper */
  label: string;
  /** An event that is triggered on change */
  onChange?: (value: Content.Properties.Brand) => void;
  /** An event that is triggered on click close */
  onClickClose?: () => void;
  /** An event that is triggered on click next */
  onClickNext?: () => void;
  /** An event that is triggered on click search */
  onClickSearch?: () => void;
}

const brands: {
  headline: string;
  icon: IconName;
  id: Content.Properties.Brand;
}[] = [
  {
    headline: 'Sol',
    icon: 'Sun',
    id: 'default',
  },
  {
    headline: 'Rejseklubben',
    icon: 'Luggage',
    id: 'rejseklubben',
  },
  // Removing safari as per request, but keeping the code for future use
  // {
  //   headline: 'Bravo Safari',
  //   icon: 'Safari',
  //   id: 'safari',
  // },
  {
    headline: 'Bravo Golf',
    icon: 'Golf',
    id: 'golf',
  },
];

const SearchBarBrandDropdown = forwardRef(
  (
    {
      className,
      description,
      isVisible = false,
      label,
      onChange,
      onClickClose,
      onClickNext,
      onClickSearch,
    }: SearchBarBrandDropdownProps,
    ref,
  ) => {
    const { brand } = useSearchContext();
    const isLaptop = useWindowSize('laptop');

    const { t } = useI18n();

    const handleClickClear = () => {
      onChange?.('default');
    };

    useImperativeHandle(ref, () => ({
      clear: handleClickClear,
    }));

    const renderOptions = () => {
      return (
        <React.Fragment>
          {brands.map((item, itemIndex) => (
            <button
              key={itemIndex}
              className={classNames(styles.button, {
                [styles.isActive]: brand === item.id,
              })}
              {...(brand === item.id && { 'data-brand': brand })}
              onClick={() => onChange?.(item.id)}
            >
              <Icon name={item.icon} size={24} />

              <Typography className={styles.text} tagStyle="bodySmall">
                {item.headline}
              </Typography>
            </button>
          ))}
        </React.Fragment>
      );
    };

    return (
      <div className={classNames(styles.component, className)}>
        {isLaptop ? (
          <motion.div
            animate={isVisible ? 'show' : 'hidden'}
            className={styles.desktopWrapper}
            exit="hidden"
            initial="hidden"
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            variants={{
              hidden: {
                opacity: 0,
                transitionEnd: {
                  display: 'none',
                },
                translateY: 0,
              },
              show: {
                display: 'block',
                opacity: 1,
                translateY: 0,
              },
            }}
          >
            <div className={styles.content}>
              <div className={styles.dropdownWrapper}>
                <div className={styles.options}>{renderOptions()}</div>
              </div>
            </div>
          </motion.div>
        ) : (
          <SearchBarSectionMobileWrapper
            buttonLabel={t('items.common.words.search')}
            description={description}
            isOpen={isVisible}
            label={label}
            onClickClear={handleClickClear}
            onClickClose={onClickClose}
            onClickNext={onClickNext}
            onClickSearch={onClickSearch}
            shouldTriggerSearch
            showBackButton
          >
            <div className={styles.mobileWrapper}>
              <div className={styles.optionsWrapper}>
                <Typography
                  className={styles.label}
                  tag="label"
                  tagStyle="labelMedium"
                >
                  {t('items.campaign.searchBar.brand.chooseBrand')}
                </Typography>

                <div className={styles.options}>{renderOptions()}</div>
              </div>
            </div>
          </SearchBarSectionMobileWrapper>
        )}
      </div>
    );
  },
);

SearchBarBrandDropdown.displayName = 'SearchBarBrandDropdown';

export default SearchBarBrandDropdown;
